"use client";

import { useState, useEffect, useCallback } from "react";
import { products, categories } from "../../lib/data";
import type { Product, Category } from "../../lib/types";
import ProductCard from "../../components/product-card";
import HorizontalFilter from "../../components/horizontal-filter";
import { Check, ArrowRight, Plus, Sparkles } from "lucide-react";
import { JUHUU } from "@juhuu/sdk-ts";
import { juhuu } from "../../juhuuClass";
import CTASection from "../../components/cta-section";
import { useTranslation } from "../../i18n";
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogDescription,
  DialogTitle,
} from "../../components/dialog";
import DashboardImg from "./../routes/newHomeRoute/assets/dashboard.png";

import { Button } from "../../components/button";

export default function MarketplacePage() {
  const { t } = useTranslation();
  const [filteredProducts, setFilteredProducts] =
    useState<JUHUU.Product.Object[]>();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategories, setSelectedCategories] = useState<Category[]>([]);
  const [selectedManufacturers, setSelectedManufacturers] = useState<string[]>(
    []
  );

  const [productArray, setProductArray] = useState<JUHUU.Product.Object[]>([]);
  const [selectedProduct, setSelectedProduct] =
    useState<JUHUU.Product.Object | null>(null);
  const [ctaName, setCtaName] = useState("");
  const [ctaCompany, setCtaCompany] = useState("");


  const [cursor, setCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);

  const PAGE_SIZE = 30;

  const handleCtaSubmit = (e: React.FormEvent) => {
    e.preventDefault();
  };


  const fetchProducts = useCallback(async (nextCursor?: string) => {
    setIsLoading(true);

    const query = await juhuu.products.list(
      {},
      { limit: PAGE_SIZE, cursor: nextCursor }
    );

    if (query.ok === false) {
      setIsLoading(false);
      return;
    }

    const {
      productArray: page,
      hasMore: more,
      cursor: next,
      count,
    } = query.data;

    // Merge while de-duplicating by id. Guards against the API returning an
    // overlapping product across pages and against StrictMode's double effect
    // invocation in dev, both of which would otherwise produce duplicate keys.
    setProductArray((prev) => {
      const combined = nextCursor ? [...prev, ...page] : page;
      const seen = new Set<string>();
      return combined.filter((p) => {
        if (seen.has(p.id)) return false;
        seen.add(p.id);
        return true;
      });
    });
    setHasMore(more);
    setCursor(next);
    setTotalCount(count);
    setIsLoading(false);
  }, []);

  const handleRefresh = useCallback(() => fetchProducts(), [fetchProducts]);

  const handleLoadMore = useCallback(() => {
    if (cursor) fetchProducts(cursor);
  }, [cursor, fetchProducts]);

  useEffect(() => {
    handleRefresh();
  }, [handleRefresh]);

  // Get unique manufacturers with logos
  const manufacturers = products.reduce((acc, product) => {
    const exists = acc.some((m) => m.name === product.manufacturer.name);
    if (!exists) {
      acc.push({
        name: product.manufacturer.name,
        logo: product.manufacturer.logo,
      });
    }
    return acc;
  }, [] as { name: string; logo: string }[]);

  // Apply filters
  useEffect(() => {
    let result = [...productArray];

    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        (product) =>
          product.name.toLowerCase().includes(query) ||
          product.description.en?.toLowerCase().includes(query)
      );
    }

    // Apply category filter
    // if (selectedCategories.length > 0) {
    //   result = result.filter((product) =>
    //     selectedCategories.includes(product.category)
    //   );
    // }

    // Apply manufacturer filter
    // if (selectedManufacturers.length > 0) {
    //   result = result.filter((product) =>
    //     selectedManufacturers.includes(product.manufacturer.name)
    //   );
    // }

    setFilteredProducts(result);
  }, [productArray, searchQuery, selectedCategories, selectedManufacturers]);

  const handleCategoryChange = (category: Category) => {
    setSelectedCategories((prev) =>
      prev.includes(category)
        ? prev.filter((c) => c !== category)
        : [...prev, category]
    );
  };

  const handleManufacturerChange = (manufacturer: string) => {
    setSelectedManufacturers((prev) =>
      prev.includes(manufacturer)
        ? prev.filter((m) => m !== manufacturer)
        : [...prev, manufacturer]
    );
  };

  const clearFilters = () => {
    setSearchQuery("");
    setSelectedCategories([]);
    setSelectedManufacturers([]);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header Section - Improved styling */}
      <div className="relative pt-24 pb-16">
        {/* Background gradient */}

        {/* Decorative elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-20 right-[10%] w-64 h-64 bg-white rounded-full opacity-10 animate-float-slow"></div>
          <div className="absolute bottom-10 left-[15%] w-48 h-48 bg-white rounded-full opacity-10 animate-float-slow-reverse"></div>
          <div className="absolute top-40 left-[5%] w-32 h-32 bg-white rounded-full opacity-10 animate-float-slow"></div>
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h1 className="text-4xl md:text-6xl font-bold text-black mb-6 drop-shadow-md">
            {t("routes.marketplace.heroTitle")} <br></br>
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-violet-600 via-purple-600 to-orange-500">
              {t("routes.marketplace.heroHighlight")}
            </span>
          </h1>
          <p className="text-xl text-black text-opacity-90 max-w-3xl mx-auto drop-shadow">
            {t("routes.marketplace.heroDescription")}
          </p>
        </div>

        {/* Divider with wave effect */}
        <div
          className="absolute bottom-0 left-0 w-full overflow-hidden"
          style={{ height: "40px" }}
        >
          <svg
            className="absolute bottom-0 w-full"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 1440 320"
            preserveAspectRatio="none"
            style={{ height: "40px" }}
          >
            <path
              fill="#f9fafb"
              d="M0,288L48,272C96,256,192,224,288,197.3C384,171,480,149,576,165.3C672,181,768,235,864,250.7C960,267,1056,245,1152,224C1248,203,1344,181,1392,170.7L1440,160L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"
            ></path>
          </svg>
        </div>
      </div>

      <div className="relative -mt-8 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-center">
          <div className="w-full max-w-3xl">
            {/* Sleek Compact Card */}
            <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-violet-600 via-purple-600 to-orange-500 p-[1px] shadow-2xl">
              <div className="relative bg-white rounded-2xl p-6 h-full">
                {/* Floating Icon */}

                {/* Content */}
                <div className="space-y-4">
                  <div className="flex items-center space-x-2">
                    <Sparkles className="w-5 h-5 text-purple-600" />
                    <h3 className="text-lg font-bold text-gray-900">
                      Share Your Solution
                    </h3>
                  </div>

                  <p className="text-sm text-gray-600 leading-relaxed">
                    Join our ecosystem and showcase your mobility products to
                    thousands of professionals.
                  </p>

                  <button className="w-full inline-flex items-center justify-center px-4 py-2.5 bg-gradient-to-r from-violet-600 to-purple-600 text-white font-medium rounded-lg hover:from-violet-700 hover:to-purple-700 transition-all duration-200 shadow-md hover:shadow-lg transform hover:-translate-y-0.5 text-sm">
                    <span>Go to Dashboard</span>
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </button>
                </div>

                {/* Subtle Background Pattern */}
                <div className="absolute inset-0 bg-gradient-to-br from-violet-50/50 to-purple-50/50 rounded-2xl -z-10"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Main Content */}
      <div
        id="products"
        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12"
      >
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-1">
            {t("routes.marketplace.browseTitle")}
          </h2>
          <p className="text-gray-600 mb-6">
            {t("routes.marketplace.browseDescription")}
          </p>

          {/* Horizontal Filters with integrated search */}
          <HorizontalFilter
            categories={categories}
            manufacturers={manufacturers}
            selectedCategories={selectedCategories}
            selectedManufacturers={selectedManufacturers}
            onCategoryChange={handleCategoryChange}
            onManufacturerChange={handleManufacturerChange}
            onClearFilters={clearFilters}
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
          />
        </div>

        {/* Product Count */}
        <div className="mb-6">
          <p className="text-sm text-gray-500">
            {t("routes.marketplace.showing")} {filteredProducts?.length}{" "}
            {t("routes.marketplace.of")} {totalCount}{" "}
            {t("routes.marketplace.products")}
          </p>
        </div>

        {/* Products - Maximum 3 per row */}
        {filteredProducts?.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-xl shadow-sm">
            <h3 className="text-lg font-medium text-gray-900">
              {t("routes.marketplace.noProductsTitle")}
            </h3>
            <p className="mt-2 text-sm text-gray-500">
              {t("routes.marketplace.noProductsDescription")}
            </p>
            <button
              className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
              onClick={clearFilters}
            >
              {t("routes.marketplace.clearFilters")}
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredProducts?.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
        )}

        {/* Load more — only shown when the API reports more pages and no
            client-side filter is narrowing the already-loaded results. */}
        {hasMore && !searchQuery && (
          <div className="mt-10 flex justify-center">
            <Button disabled={isLoading} onClick={handleLoadMore}>
              {isLoading
                ? t("routes.marketplace.loading")
                : t("routes.marketplace.loadMore")}
            </Button>
          </div>
        )}
      </div>
      <Dialog
        open={selectedProduct !== null}
        onClose={() => setSelectedProduct(null)}
        style={{
          zIndex: 100,
          position: "absolute",
        }}
        size="3xl"
      >
        <DialogTitle>{selectedProduct?.name}</DialogTitle>
        <DialogDescription>{selectedProduct?.previewText.en}</DialogDescription>
        <DialogBody>
          <img
            className="w-full"
            src={selectedProduct?.bannerImageLight[0]}
            alt={t("routes.marketplace.productImageAlt")}
          />
        </DialogBody>
        <DialogActions>
          <Button plain onClick={() => setSelectedProduct(null)}>
            {t("routes.marketplace.close")}
          </Button>
          {selectedProduct?.articleId !== null && (
            <Button
              onClick={() =>
                window.open(
                  "https://docs.juhuu.app/articles/" +
                    selectedProduct?.articleId,
                  "_blank"
                )
              }
            >
              {t("routes.marketplace.readMore")}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* New CTA Section */}
      <section className="relative w-full mt-16">
        {/* Hintergrund mit Farbverlauf */}
        <div className="absolute inset-0 bg-gradient-to-br from-purple-700 via-purple-800 to-navy-900"></div>

        {/* Dekorative Elemente */}
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
          <div className="absolute -top-24 -right-24 w-96 h-96 bg-purple-400 rounded-full opacity-10"></div>
          <div className="absolute top-1/2 -left-24 w-64 h-64 bg-blue-400 rounded-full opacity-10"></div>
          <div className="absolute -bottom-32 right-1/3 w-80 h-80 bg-indigo-300 rounded-full opacity-10"></div>
        </div>

        {/* Organischer Übergang zum Footer */}
        <div
          className="absolute bottom-0 left-0 w-full overflow-hidden"
          style={{ height: "150px" }}
        >
          <svg
            className="absolute bottom-0 w-full"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 1440 320"
            preserveAspectRatio="none"
            style={{ height: "150px" }}
          >
            {/* Erste Welle - Hintergrund */}
            <path
              fill="rgba(236, 72, 153, 0.2)"
              d="M0,224L40,213.3C80,203,160,181,240,181.3C320,181,400,203,480,208C560,213,640,203,720,202.7C800,203,880,213,960,229.3C1040,245,1120,267,1200,261.3C1280,256,1360,224,1400,208L1440,192L1440,320L1400,320C1360,320,1280,320,1200,320C1120,320,1040,320,960,320C880,320,800,320,720,320C640,320,560,320,480,320C400,320,320,320,240,320C160,320,80,320,40,320L0,320Z"
            ></path>

            {/* Zweite Welle - Mittlere Ebene */}
            <path
              fill="rgba(139, 92, 246, 0.25)"
              d="M0,256L40,261.3C80,267,160,277,240,272C320,267,400,245,480,240C560,235,640,245,720,245.3C800,245,880,235,960,213.3C1040,192,1120,160,1200,165.3C1280,171,1360,213,1400,234.7L1440,256L1440,320L1400,320C1360,320,1280,320,1200,320C1120,320,1040,320,960,320C880,320,800,320,720,320C640,320,560,320,480,320C400,320,320,320,240,320C160,320,80,320,40,320L0,320Z"
            ></path>

            {/* Dritte Welle - Vordergrund, die in den Footer übergeht */}
            <path
              fill="url(#gradient-footer)"
              d="M0,288L40,282.7C80,277,160,267,240,250.7C320,235,400,213,480,213.3C560,213,640,235,720,245.3C800,256,880,256,960,234.7C1040,213,1120,171,1200,165.3C1280,160,1360,192,1400,208L1440,224L1440,320L1400,320C1360,320,1280,320,1200,320C1120,320,1040,320,960,320C880,320,800,320,720,320C640,320,560,320,480,320C400,320,320,320,240,320C160,320,80,320,40,320L0,320Z"
            ></path>

            <defs>
              <linearGradient
                id="gradient-footer"
                x1="0%"
                y1="0%"
                x2="100%"
                y2="0%"
              >
                <stop offset="0%" stopColor="#ff0080" stopOpacity="0.7" />
                <stop offset="50%" stopColor="#7928ca" stopOpacity="0.7" />
                <stop offset="100%" stopColor="#0A2540" stopOpacity="0.9" />
              </linearGradient>
            </defs>
          </svg>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 pt-20 pb-40">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                {t("routes.marketplace.ctaTitle")}
              </h2>
              <p className="text-xl text-purple-100 mb-8">
                {t("routes.marketplace.ctaSubTitle")}
              </p>

              <div className="space-y-4 mb-8">
                <div className="flex items-start">
                  <div className="flex-shrink-0 h-6 w-6 rounded-full bg-gradient-to-r from-green-400 to-emerald-500 flex items-center justify-center mr-3">
                    <Check className="h-4 w-4 text-white" />
                  </div>
                  <p className="text-white">
                    {t("routes.marketplace.ctaLaunch")}
                  </p>
                </div>
                <div className="flex items-start">
                  <div className="flex-shrink-0 h-6 w-6 rounded-full bg-gradient-to-r from-green-400 to-emerald-500 flex items-center justify-center mr-3">
                    <Check className="h-4 w-4 text-white" />
                  </div>
                  <p className="text-white">
                    {t("routes.marketplace.ctaScale")}
                  </p>
                </div>
                <div className="flex items-start">
                  <div className="flex-shrink-0 h-6 w-6 rounded-full bg-gradient-to-r from-green-400 to-emerald-500 flex items-center justify-center mr-3">
                    <Check className="h-4 w-4 text-white" />
                  </div>
                  <p className="text-white">{t("routes.marketplace.ctaPay")}</p>
                </div>
              </div>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 shadow-2xl border border-white/10">
              <h3 className="text-xl font-bold text-white mb-6">
                {t("routes.marketplace.ctaStart")}
              </h3>
              <form className="space-y-4" onSubmit={handleCtaSubmit}>
                <div>
                  <label
                    htmlFor="name"
                    className="block text-sm font-medium text-purple-100 mb-1"
                  >
                    {t("routes.marketplace.ctaFullName")}
                  </label>
                  <input
                    type="text"
                    id="name"
                    className="w-full px-4 py-3 bg-white/5 border border-purple-400/30 rounded-md text-white placeholder-purple-200/50 focus:outline-none focus:ring-2 focus:ring-purple-500"
                    placeholder={t("routes.marketplace.ctaNamePlaceholder")}
                    value={ctaName}
                    onChange={(e) => setCtaName(e.target.value)}
                  />
                </div>

                <div>
                  <label
                    htmlFor="company"
                    className="block text-sm font-medium text-purple-100 mb-1"
                  >
                    {t("routes.marketplace.ctaCompany")}
                  </label>
                  <input
                    type="text"
                    id="company"
                    className="w-full px-4 py-3 bg-white/5 border border-purple-400/30 rounded-md text-white placeholder-purple-200/50 focus:outline-none focus:ring-2 focus:ring-purple-500"
                    placeholder={t("routes.marketplace.ctaCompanyPlaceholder")}
                    value={ctaCompany}
                    onChange={(e) => setCtaCompany(e.target.value)}
                  />
                </div>

                <button
                  type="submit"
                  className="w-full inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 transition-colors shadow-lg disabled:opacity-50"
                >
                  {t("routes.marketplace.ctaSubmit")}{" "}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </button>

                <p className="text-xs text-purple-200/70 text-center mt-4">
                  {t("routes.marketplace.ctaConsent")}{" "}
                  <a href="/terms" className="underline hover:text-white">
                    {t("routes.marketplace.ctaTerms")}
                  </a>{" "}
                  {t("routes.marketplace.ctaAnd")}{" "}
                  <a href="/privacy" className="underline hover:text-white">
                    {t("routes.marketplace.ctaPrivacy")}
                  </a>
                  .
                </p>
              </form>
            </div>
          </div>
        </div>
      </section>
      <div id="contact">
        <CTASection />
      </div>
    </div>
  );
}
